import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { constructQueryParamName, isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { UserType } from '../../util/enums';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { retrieveUserFavoritesList } from '../../util/userDataExtraction';
import { addToFavorites } from '../FavoritesPage/FavoritesPage.duck';
import { entityRefs, getStateFromHistory, isArrayLength } from '../../util/genericHelpers';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { pick } from 'lodash';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //
export const SET_INITIAL_VALUES = 'app/SearchPage/SET_INITIAL_VALUES';

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_LISTINGS_DATA = {
  REQUEST: 'app/SearchPage/FETCH_LISTINGS_DATA_REQUEST',
  SUCCESS: 'app/SearchPage/FETCH_LISTINGS_DATA_SUCCESS',
  ERROR: 'app/SearchPage/FETCH_LISTINGS_DATA_ERROR',
};
// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  fetchListingDataInProgress: false,
  fetchListingDataError: null,
  listingData: null,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FETCH_LISTINGS_DATA.REQUEST:
      return {
        ...state,
        fetchListingDataInProgress: true,
        fetchListingDataError: null,
        listingData: null,
      };
    case FETCH_LISTINGS_DATA.SUCCESS:
      return {
        ...state,
        fetchListingDataInProgress: false,
        fetchListingDataError: null,
        listingData: payload,
      };
    case FETCH_LISTINGS_DATA.ERROR:
      return {
        ...state,
        fetchListingDataInProgress: false,
        fetchListingDataError: payload,
        listingData: null,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Selectors ================ //
export const customInterestsSelector = state => {
  const listingData = state.SearchPage.listingData;
  return listingData?.customInterests || [];
};

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchListingsDataRequest = () => ({
  type: FETCH_LISTINGS_DATA.REQUEST,
});

export const fetchListingsDataSuccess = data => ({
  type: FETCH_LISTINGS_DATA.SUCCESS,
  payload: data,
});

export const fetchListingsDataError = e => ({
  type: FETCH_LISTINGS_DATA.ERROR,
  error: true,
  payload: e,
});

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = params?.[levelKey];
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const { perPage, price, dates, sort, mapSearch, pub_interest, ...restOfParams } = searchParams;
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };
  const params = {
    // pub_listingCategory: UserType.TALENT,
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
    ...(pub_interest && { pub_interest: `has_any:${pub_interest}` }),
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const fetchListingsData = () => async (dispatch, getState, sdk) => {
  dispatch(fetchListingsDataRequest());

  try {
    const listings = [];
    const batchSize = 10;
    const delay = 500;

    const queryParams = {
      page: 1,
      perPage: batchSize,
      pub_listingCategory: UserType.TALENT,
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
      ],
    };

    const firstResponse = await sdk.listings.query(queryParams);
    const { data, meta } = firstResponse.data || {};
    const totalPages = meta?.totalPages || 0;
    if (!data?.length) {
      dispatch(fetchListingsDataSuccess());
      return;
    }

    let entities = updatedEntities({}, firstResponse.data);
    listings.push(...denormalisedEntities(entities, entityRefs(data), false));

    for (let i = 2; i <= totalPages; i++) {
      const res = await sdk.listings.query({ ...queryParams, page: i });
      entities = updatedEntities(entities, res.data);
      listings.push(...denormalisedEntities(entities, entityRefs(res.data?.data || []), false));
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    // 🔧 Flatten all nested key_responsibilities arrays
    const customInterests = listings
      .flatMap(l => l.attributes?.publicData?.customInterests || [])
      .filter(Boolean);
    // ✅ Deduplicate
    const uniqueCustomInterests = Array.from(new Set(customInterests));

    const listingData = {
      customInterests: uniqueCustomInterests,
    };
    dispatch(fetchListingsDataSuccess(listingData));
    return { customInterests: uniqueCustomInterests };
  } catch (err) {
    dispatch(fetchListingsDataError(storableError(err)));
    throw err;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch(
    setInitialValues({
      pagination: null,
      currentPageResultIds: [],
    })
  );
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const {
    page = 1,
    address,
    origin,
    pub_pub_sports,
    pub_customInterests,
    pub_autographsSubTypes,
    pub_shoutoutsSubTypes,
    pub_enhancedServicesSubTypes,
    pub_appearancesSubTypes,
    pub_endorsementsSubTypes,
    pub_creativeMediaSubTypes,
    pub_isExclusiveTraining,
    pub_isMentorship,
    pub_isBusinessDevelopment,
    pub_isUnlockableContent,
    pub_unlockableTypes,
    ...rest
  } = queryParams;
  const stateFromHistory = getStateFromHistory();
  const listingIds = stateFromHistory?.state?.ids || [];
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const currentUser = await dispatch(fetchCurrentUser());
  const favorites = retrieveUserFavoritesList(currentUser) || [];
  const idsArray = isArrayLength(favorites) && favorites;
  dispatch(fetchListingsData());
  if (isArrayLength(idsArray)) {
    dispatch(addToFavorites(favorites));
  }
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('activeCategory');
  }
  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      ...(isArrayLength(listingIds) && { ids: listingIds }),
      ...(pub_pub_sports ? { pub_pub_sports: `has_any:${pub_pub_sports}` } : {}),
      ...(pub_customInterests ? { pub_customInterests: `has_any:${pub_customInterests}` } : {}),
      ...(pub_autographsSubTypes
        ? { pub_autographsSubTypes: `has_any:${pub_autographsSubTypes}` }
        : {}),
      ...(pub_shoutoutsSubTypes
        ? { pub_shoutoutsSubTypes: `has_any:${pub_shoutoutsSubTypes}` }
        : {}),
      ...(pub_enhancedServicesSubTypes
        ? { pub_enhancedServicesSubTypes: `has_any:${pub_enhancedServicesSubTypes}` }
        : {}),
      ...(pub_appearancesSubTypes
        ? { pub_appearancesSubTypes: `has_any:${pub_appearancesSubTypes}` }
        : {}),
      ...(pub_creativeMediaSubTypes
        ? {
            pub_creativeMediaSubTypes: `has_any:${pub_creativeMediaSubTypes}`,
          }
        : {}),
      ...(pub_endorsementsSubTypes
        ? {
            pub_endorsementsSubTypes: `has_any:${pub_endorsementsSubTypes}`,
          }
        : {}),
      ...(pub_isMentorship
        ? {
            isEnhancedServices: pub_isMentorship,
          }
        : {}),
      ...(pub_isExclusiveTraining
        ? {
            isEnhancedServices: pub_isExclusiveTraining,
          }
        : {}),
      ...(pub_isBusinessDevelopment
        ? {
            isEnhancedServices: pub_isBusinessDevelopment,
          }
        : {}),
      ...(pub_unlockableTypes
        ? {
            pub_unlockableTypes: `has_any:${pub_unlockableTypes}`,
          }
        : {}),

      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [
        'variants.default',
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );
  return dispatch(searchListingsCall);
};
